import { useMutation } from "@apollo/client";

import { DELETE_INGREDIENT_STEP, DELETE_INGREDIENT_SUBSTEP, DELETE_INSTRUCTION_STEP, DELETE_INSTRUCTION_SUBSTEP, DELETE_OUTSIDE_INSTRUCTION_STEP, DELETE_OUTSIDE_INSTRUCTION_SUBSTEP, DELETE_PRESET_STEP, DELETE_PRESET_SUBSTEP, UPDATE_RECIPE_LAST_UPDATED_AT_AND_TAGS } from "../app/graphql/mutations";
import { Recipe, Substep } from "../app/recipeCreation/createRecipe.types";
import cookingTimeCalculation from "../utils/cookingTimeUtil";
import { chooseDeleteType, getUpdatedRecipeVarSteps, getUpdatedStepSubsteps } from "./utils/useDeleteSubstep.utils";
import { getSelectedRecipeVariation, getSelectedRecipeVariationSteps, getSelectedStep, removeIngredientUpdatedIngredientIdList, updateRecipeLastUpdatedAtAndTags } from "./utils/useSaveSubstep.utils";
import { getNutrientInfo } from "../utils/experience2/ingredientsUtil";
import { ingredientsObjectSelector } from "../app/commonSelector";
import { useAppSelector } from "../app/store/hooks";
import { IngredientsInterface } from "../app/homepage/homepage.types";

const deletePresetStep = async ({
  oldRecipe,
  stepId,
  recipeVariationId,
  substep,
  subStepUId,
  oldSubstepId,
  deleteStepAt,
  deletePresetStepMutation
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  substep: Substep,
  subStepUId: string,
  oldSubstepId: string,
  deleteStepAt: number,
  deletePresetStepMutation: any
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);

    if (stepSubStepToUpdate) {
      const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([substep]) / 60);
      const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep : 0;
      const updatedRecipeVarSteps = getUpdatedRecipeVarSteps(recipeVariationSteps, deleteStepAt)

      await deletePresetStepMutation({
        variables: {
          stepId,
          stepSubstepId: stepSubStepToUpdate.id,
          recipeVarStepId: recipeVarStep.id,
          recipeVarSteps: updatedRecipeVarSteps,
          recipeVariationId,
          cooking_time: updatedCookingTime,
        },
      })
    }
  }
}

const deleteInstructionStep = async ({
  oldRecipe,
  stepId,
  recipeVariationId,
  substep,
  subStepUId,
  oldSubstepId,
  deleteStepAt,
  deleteInstructionStepMutation
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  substep: Substep,
  subStepUId: string,
  oldSubstepId: string,
  deleteStepAt: number,
  deleteInstructionStepMutation: any
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);

    if (stepSubStepToUpdate) {
      const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([substep]) / 60);
      const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep : 0;
      const updatedRecipeVarSteps = getUpdatedRecipeVarSteps(recipeVariationSteps, deleteStepAt)
      await deleteInstructionStepMutation({
        variables: {
          stepId,
          stepSubstepId: stepSubStepToUpdate.id,
          recipeVarStepId: recipeVarStep.id,
          recipeVarSteps: updatedRecipeVarSteps,
          recipeVariationId,
          cooking_time: updatedCookingTime,
          substepId: oldSubstepId
        }
      })
    }
  }
}

const deleteIngredientStep = async ({
  oldRecipe,
  stepId,
  recipeVariationId,
  substep,
  subStepUId,
  oldSubstepId,
  deleteStepAt,
  deleteIngredientStepMutation,
  ingredientsObject
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  substep: Substep,
  subStepUId: string,
  oldSubstepId: string,
  deleteStepAt: number,
  deleteIngredientStepMutation: any,
  ingredientsObject: IngredientsInterface
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);
  const updatedRecipeVarIngredientsList = removeIngredientUpdatedIngredientIdList(oldRecipe, recipeVariationId, subStepUId)
  const substepIngredientIdsToDelete = substep.SubSteps_Ingredients_E3s.map((ingredient) => ingredient.id)
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, ingredientsObject, null, substep);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo };

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);

    if (stepSubStepToUpdate) {
      const updatedRecipeVarSteps = getUpdatedRecipeVarSteps(recipeVariationSteps, deleteStepAt)
      await deleteIngredientStepMutation({
        variables: {
          stepId,
          stepSubstepId: stepSubStepToUpdate.id,
          recipeVarStepId: recipeVarStep.id,
          recipeVarSteps: updatedRecipeVarSteps,
          substepId: oldSubstepId,
          recipeVariationId,
          substepIngredientIdsToDelete,
          newIngredientsList: updatedRecipeVarIngredientsList,
          updatedMetadata
        }
      })
    }
  }
}

const deleteOutsideInstructionStep = async ({
  oldRecipe,
  stepId,
  recipeVariationId,
  subStepUId,
  oldSubstepId,
  deleteStepAt,
  deleteOutsideInstructionStepMutation
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  subStepUId: string,
  oldSubstepId: string,
  deleteStepAt: number,
  deleteOutsideInstructionStepMutation: any
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);

    if (stepSubStepToUpdate) {
      const updatedRecipeVarSteps = getUpdatedRecipeVarSteps(recipeVariationSteps, deleteStepAt)

      await deleteOutsideInstructionStepMutation({
        variables: {
          stepId,
          stepSubstepId: stepSubStepToUpdate.id,
          recipeVarStepId: recipeVarStep.id,
          recipeVarSteps: updatedRecipeVarSteps,
          substepId: oldSubstepId
        }
      })
    }
  }
}

const deletePresetSubstep = async ({
  oldRecipe,
  stepId,
  recipeVariationId,
  substep,
  subStepUId,
  oldSubstepId,
  deleteSubStepAt,
  deletePresetSubstepMutation
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  substep: Substep,
  subStepUId: string,
  oldSubstepId: string,
  deleteSubStepAt: number,
  deletePresetSubstepMutation: any
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);
    const updatedStepSubsteps = getUpdatedStepSubsteps(recipeVarStep, deleteSubStepAt)
    if (stepSubStepToUpdate) {
      const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([substep]) / 60);
      const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep : 0;
      await deletePresetSubstepMutation({
        variables: {
          stepSubstepId: stepSubStepToUpdate.id,
          recipeVariationId,
          cooking_time: updatedCookingTime,
          stepSubstepUpdates: updatedStepSubsteps,
        }
      })
    }
  }
}

const deleteInstructionSubstep = async ({
  oldRecipe,
  stepId,
  recipeVariationId,
  substep,
  subStepUId,
  oldSubstepId,
  deleteSubStepAt,
  deleteInstructionSubstepMutation
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  substep: Substep,
  subStepUId: string,
  oldSubstepId: string,
  deleteSubStepAt: number,
  deleteInstructionSubstepMutation: any
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);
    const updatedStepSubsteps = getUpdatedStepSubsteps(recipeVarStep, deleteSubStepAt)
    if (stepSubStepToUpdate) {
      const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([substep]) / 60);
      const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep : 0;
      await deleteInstructionSubstepMutation({
        variables: {
          stepSubstepId: stepSubStepToUpdate.id,
          recipeVariationId,
          cooking_time: updatedCookingTime,
          stepSubstepUpdates: updatedStepSubsteps,
          substepId: oldSubstepId
        }
      })
    }
  }
}

const deleteIngredientSubstep = async ({
  oldRecipe,
  stepId,
  recipeVariationId,
  substep,
  subStepUId,
  oldSubstepId,
  deleteSubStepAt,
  deleteIngredientSubstepMutation,
  ingredientsObject
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  substep: Substep,
  subStepUId: string,
  oldSubstepId: string,
  deleteSubStepAt: number,
  deleteIngredientSubstepMutation: any,
  ingredientsObject: IngredientsInterface

}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);
  const updatedRecipeVarIngredientsList = removeIngredientUpdatedIngredientIdList(oldRecipe, recipeVariationId, subStepUId)
  const substepIngredientIdsToDelete = substep.SubSteps_Ingredients_E3s.map((ingredient) => ingredient.id)
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, ingredientsObject, null, substep);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo };
  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);
    const updatedStepSubsteps = getUpdatedStepSubsteps(recipeVarStep, deleteSubStepAt)

    if (stepSubStepToUpdate) {
      await deleteIngredientSubstepMutation({
        variables: {
          stepSubstepId: stepSubStepToUpdate.id,
          stepSubstepUpdates: updatedStepSubsteps,
          substepId: oldSubstepId,
          recipeVariationId,
          substepIngredientIdsToDelete,
          newIngredientsList: updatedRecipeVarIngredientsList,
          updatedMetadata
        }
      })
    }
  }
}

const deleteOutsideInstructionSubstep = async ({
  oldRecipe,
  stepId,
  recipeVariationId,
  subStepUId,
  oldSubstepId,
  deleteSubStepAt,
  deleteOutsideInstructionSubstepMutation
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  subStepUId: string,
  oldSubstepId: string,
  deleteSubStepAt: number,
  deleteOutsideInstructionSubstepMutation: any
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);
    const updatedStepSubsteps = getUpdatedStepSubsteps(recipeVarStep, deleteSubStepAt)
    if (stepSubStepToUpdate) {

      await deleteOutsideInstructionSubstepMutation({
        variables: {
          stepSubstepId: stepSubStepToUpdate.id,
          stepSubstepUpdates: updatedStepSubsteps,
          substepId: oldSubstepId
        }
      })
    }
  }
}

const useDeleteSubstep = () => {

  const [deletePresetStepMutation, { loading: deletingPresetStep }] = useMutation(DELETE_PRESET_STEP);
  const [deleteInstructionStepMutation, { loading: deletingInstructionStep }] = useMutation(DELETE_INSTRUCTION_STEP);
  const [deleteIngredientStepMutation, { loading: deletingIngredientStep }] = useMutation(DELETE_INGREDIENT_STEP);
  const [deleteOutsideInstructionStepMutation, { loading: deletingOutsideInstructionStep }] = useMutation(DELETE_OUTSIDE_INSTRUCTION_STEP);

  const [deletePresetSubstepMutation, { loading: deletingPresetSubstep }] = useMutation(DELETE_PRESET_SUBSTEP);
  const [deleteInstructionSubstepMutation, { loading: deletingInstructionSubstep }] = useMutation(DELETE_INSTRUCTION_SUBSTEP);
  const [deleteIngredientSubstepMutation, { loading: deletingIngredientSubstep }] = useMutation(DELETE_INGREDIENT_SUBSTEP);
  const [deleteOutsideInstructionSubstepMutation, { loading: deletingOutsideInstructionSubstep }] = useMutation(DELETE_OUTSIDE_INSTRUCTION_SUBSTEP);
  const [updateLastUpdatedAtAndTags, { loading: updatingRecipeLastUpdatedAtAndTags }] = useMutation(UPDATE_RECIPE_LAST_UPDATED_AT_AND_TAGS);
  const ingredientsObject = useAppSelector(ingredientsObjectSelector);

  const onDelete = async ({
    oldRecipe,
    stepId,
    recipeVariationId,
    substep,
    subStepUId,
    deleteStepAt,
    deleteSubStepAt,
    isDeleteStep,
    onCompleted,
    onError,
  }: {
    oldRecipe: Recipe,
    stepId: string,
    recipeVariationId: string,
    substep: Substep,
    subStepUId: string,
    deleteStepAt?: number,
    deleteSubStepAt?: number,
    isDeleteStep: boolean,
    onCompleted: () => void,
    onError: (err: any) => void
  }) => {
    try {

      const { id: oldSubstepId, type: substepType, Recipe_Presets: recipePresets } = substep;
      const isPreset = recipePresets.length > 0;
      switch (chooseDeleteType({ isDeleteStep, substepType, isPreset })) {

        case "deletePresetStep":
          if (deleteStepAt !== undefined) {
            await deletePresetStep({
              oldRecipe,
              stepId,
              recipeVariationId,
              substep,
              subStepUId,
              oldSubstepId,
              deleteStepAt,
              deletePresetStepMutation
            })
          }
          break;

        case "deleteInstructionStep":
          if (deleteStepAt !== undefined) {
            await deleteInstructionStep({
              oldRecipe,
              stepId,
              recipeVariationId,
              substep,
              subStepUId,
              oldSubstepId,
              deleteStepAt,
              deleteInstructionStepMutation
            })
          }

          break;

        case "deleteIngredientStep":
          if (deleteStepAt !== undefined) {
            await deleteIngredientStep({
              oldRecipe,
              stepId,
              recipeVariationId,
              substep,
              subStepUId,
              oldSubstepId,
              deleteStepAt,
              deleteIngredientStepMutation,
              ingredientsObject
            })
          }
          break;

        case "deleteOutsideInstructionStep":
          if (deleteStepAt !== undefined) {
            await deleteOutsideInstructionStep({
              oldRecipe,
              stepId,
              recipeVariationId,
              subStepUId,
              oldSubstepId,
              deleteStepAt,
              deleteOutsideInstructionStepMutation
            })
          }

          break;

        case "deletePreset":
          if (deleteSubStepAt !== undefined) {
            await deletePresetSubstep({
              oldRecipe,
              stepId,
              recipeVariationId,
              substep,
              subStepUId,
              oldSubstepId,
              deleteSubStepAt,
              deletePresetSubstepMutation
            })
          }

          break;

        case "deleteInstruction":
          if (deleteSubStepAt !== undefined) {
            await deleteInstructionSubstep({
              oldRecipe,
              stepId,
              recipeVariationId,
              substep,
              subStepUId,
              oldSubstepId,
              deleteSubStepAt,
              deleteInstructionSubstepMutation
            })
          }

          break;

        case "deleteIngredient":
          if (deleteSubStepAt !== undefined) {
            await deleteIngredientSubstep({
              oldRecipe,
              stepId,
              recipeVariationId,
              substep,
              subStepUId,
              oldSubstepId,
              deleteSubStepAt,
              deleteIngredientSubstepMutation,
              ingredientsObject
            })
          }
          break;

        case "deleteOutsideInstruction":
          if (deleteSubStepAt !== undefined) {
            await deleteOutsideInstructionSubstep({
              oldRecipe,
              stepId,
              recipeVariationId,
              subStepUId,
              oldSubstepId,
              deleteSubStepAt,
              deleteOutsideInstructionSubstepMutation
            })
          }
          break;

        default:
          break;
      }
      await updateRecipeLastUpdatedAtAndTags({ oldRecipe, updateLastUpdatedAtAndTags });

      onCompleted();
    } catch (err) {

      console.error("error deleting substep", err)
      onError(err);
    }
  }
  return {
    onDelete,
    loading: deletingPresetStep ||
      deletingInstructionStep ||
      deletingOutsideInstructionStep ||
      deletingIngredientStep ||
      deletingPresetSubstep ||
      deletingInstructionSubstep ||
      deletingIngredientSubstep ||
      deletingOutsideInstructionSubstep ||
      updatingRecipeLastUpdatedAtAndTags

  };
}

export default useDeleteSubstep;